import { GavelOutlined as GavelOutlinedIcon } from 'components/icons';
import { CustomTableEmptyStateProps } from 'components/tables/list-table/ListTableTypes';
import CircularLoader from 'components/CircularLoader';
import Loader from 'components/Loader';
import EmptyState from 'components/EmptyState';
import ScrollX from 'components/ScrollX';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { getSortedFilterOptions } from 'components/tables/list-table/filterUtil.ts';
import { EndOfListSentinel } from 'components/tables/EndOfListSentinel';
import BoxLoader from 'components/tables/list-table/loader/BoxLoader';
import { useCallback, useEffect, useMemo } from 'react';
import {
    useLazyGetBillableMattersCountQuery,
    useLazyGetBillableMattersWithFiltersQuery,
} from 'store/api/billable-matters';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetAllEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useGetInvoiceTemplatesQuery } from 'store/api/invoices';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { BillableMatterListItem } from 'types/billable-matters';
import { QueryResponse } from 'types/paginationAndFilter';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { useClassifiers } from 'utils/useClassifiers';
import { BillableMattersGroupTable, GroupedMatters } from './BillableMattersGroupTable';

const groupCases = (matters: BillableMatterListItem[]) => {
    const groupedMatters = groupCasesByClient(matters);

    return { groupedMatters };
};

const groupCasesByClient = (matters: BillableMatterListItem[]) => {
    const groupedMatters: Map<number, GroupedMatters> = new Map();
    matters.forEach((matter) => {
        if (!matter.clientParty) return;
        const group = groupedMatters.get(matter.clientParty?.id);
        groupedMatters.set(matter.clientParty?.id, {
            client: matter.clientParty,
            matters: (group?.matters || []).concat(matter),
        });
    });

    return groupedMatters;
};

const BillableMattersTable = ({
    disableUrlState = false,
    handleMatterSelect,
    handleClientSelectAll,
    renderCustomHeader,
    setExcludedFromJointInvoicing,
    selectedIds,
    onSelectionChange,
}: {
    disableUrlState?: boolean;
    handleMatterSelect: (id: number, selected: boolean) => void;
    handleClientSelectAll: (matterIds: number[], selected: boolean) => void;
    setExcludedFromJointInvoicing: (value: BillableMatterListItem[]) => void;
    selectedIds?: Set<number>;
    onSelectionChange?: (selectedIds: Set<number>) => void;
    renderCustomHeader?: (fetchFilteredData: (searchText: string) => void, searchText: string) => JSX.Element;
}) => {
    // Build default filters for billable clients (billable matters by default)
    const initialSearchParams = 'qf=matterStats=isbillable=true';

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['CURRENCY', 'INVOICING_INTERVAL_TYPE', 'INVOICE_STATUS_TYPE', 'PRICING_LIMITATION_TYPE'],
    });
    const { data: invoiceTemplateList, isLoading: isLoadingInvoiceTemplates } = useGetInvoiceTemplatesQuery();

    const [fetchData, { data, isFetching }] = useLazyGetBillableMattersWithFiltersQuery();

    // TODO(react-doctor/no-prop-callback-in-effect): lift `data` into the parent so both sides
    // read the same source instead of syncing via setExcludedFromJointInvoicing.
    useEffect(() => {
        if (data) {
            setExcludedFromJointInvoicing(data.content.filter((matter) => matter.isJointInvoiceDisabled));
        }
    }, [data, setExcludedFromJointInvoicing]);

    const renderMatterTable = useCallback(
        (
            dataToDisplay: BillableMatterListItem[],
            initialLoad: boolean,
            noData: boolean,
            textId?: string,
            paginationProps?: Partial<QueryResponse<BillableMatterListItem>>,
            handleLoadMore?: () => void,
            emptyStateProps?: CustomTableEmptyStateProps
        ) => {
            const matters = dataToDisplay ? Object.values(dataToDisplay).flat() : [];
            const { groupedMatters } = groupCases(matters);
            const billableMattersArray = Array.from(groupedMatters);

            if (initialLoad) {
                return <Loader />;
            }

            return (
                <ScrollX className="flex">
                    <div className="flex w-full flex-col overflow-y-hidden">
                        <div className="border-divider flex w-full overflow-x-auto overflow-y-hidden rounded-[4px] border-y">
                            {/* z-12 is a local level, not an app tier: it keeps the veil under the list's
                                sticky filter bar (z-30) while covering the groups. */}
                            {isFetching && billableMattersArray.length > 0 ? (
                                <CircularLoader className="bg-divider/40 z-12" />
                            ) : null}
                            {noData ? (
                                <EmptyState
                                    size="page"
                                    variant={emptyStateProps?.variant ?? 'empty'}
                                    icon={GavelOutlinedIcon}
                                    titleId={textId}
                                    bodyId="billable-clients.empty.body"
                                    surface="billable-matters"
                                    onClearFilters={emptyStateProps?.onClearFilters}
                                    onRetry={emptyStateProps?.onRetry}
                                />
                            ) : (
                                <div className="h-full w-full overflow-x-auto py-1 pr-0.5">
                                    {billableMattersArray.map(([id, group]) => (
                                        <BillableMattersGroupTable
                                            classifiers={classifiers}
                                            key={`matter-group-${id}`}
                                            matterGroup={group}
                                            handleMatterSelect={handleMatterSelect}
                                            selectedIds={selectedIds ?? new Set()}
                                            handleClientSelectAll={handleClientSelectAll}
                                        />
                                    ))}
                                    {isFetching ? <BoxLoader amountOfColumns={3} height={120} /> : null}
                                    {/* Last child of the scroller, so reaching the end of the loaded
                                        groups pulls the next page. Outside the container it would
                                        never be clipped, and so would fire on the first frame. */}
                                    <EndOfListSentinel
                                        rowCount={dataToDisplay.length}
                                        hasNextPage={!!paginationProps && !paginationProps.last}
                                        isLoadingMore={isFetching}
                                        onLoadMore={handleLoadMore}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </ScrollX>
            );
        },
        [isFetching, classifiers, handleMatterSelect, selectedIds, handleClientSelectAll]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                {
                    selector: 'caseNumber',
                    comperator: '=ismycase=',
                    filters: [
                        {
                            tooltip: 'matters.tooltips.my-cases',
                            label: t('matters.my-cases'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    selector: 'caseNumber',
                    comperator: '=ismyteamcase=',
                    filters: [
                        {
                            tooltip: 'matters.tooltips.my-team-cases',
                            label: t('matters.my-team-cases'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    selector: 'matterStats',
                    comperator: '=isbillable=',
                    filters: [
                        {
                            tooltip: 'matters.tooltips.billable-cases',
                            label: t('matters.billable-cases'),
                            argument: 'true',
                        },
                    ],
                },
            ],
            filters: [
                // Invoice section
                {
                    type: 'checkboxgroup',
                    selector: 'matterStats',
                    comperator: '=isbillable=',
                    group: 'billable-clients.filter.groups.invoice',
                    filters: [
                        {
                            tooltip: 'matters.tooltips.billable-cases',
                            label: t('matters.billable-cases'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'dropdown',
                    selector: 'id',
                    comperator: '=isjointinvoiced=',
                    group: 'billable-clients.filter.groups.invoice',
                    label: t('matters.joint-invoicing'),
                    options: [
                        {
                            label: t('yes'),
                            value: 'true',
                        },
                        {
                            label: t('no'),
                            value: 'false',
                        },
                    ],
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'billable-clients.filter.groups.invoice',
                    label: t('billable-clients.filter.labels.invoiced-by-office'),
                    selector: 'invoicedByOffice.id',
                    paginated: {
                        fetch: useLazyGetMinOfficesWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                    getOptionEndAdornment: officeInactiveSuffix,
                },
                {
                    type: 'multiselect',
                    group: 'billable-clients.filter.groups.invoice',
                    label: t('billable-clients.filter.labels.invoicing-interval'),
                    selector: 'invoicingIntervalTypeCode',
                    options:
                        (getOptionsFromClassifier(classifiers['INVOICING_INTERVAL_TYPE']) as {
                            label: string;
                            value: string;
                        }[]) ?? [],
                },
                {
                    type: 'checkboxgroup',
                    group: 'billable-clients.filter.groups.invoice',
                    subgroup: 'invoice-status',
                    label: t('billable-clients.filter.labels.invoice-status'),
                    selector: 'invoices.invoiceStatusTypeCode',
                    multiple: true,
                    filters: [
                        {
                            label: getClassifierText(classifiers['INVOICE_STATUS_TYPE'], 'DRAFT'),
                            argument: 'DRAFT',
                        },
                        {
                            label: getClassifierText(classifiers['INVOICE_STATUS_TYPE'], 'REVIEWED'),
                            argument: 'REVIEWED',
                        },
                        {
                            label: getClassifierText(classifiers['INVOICE_STATUS_TYPE'], 'APPROVED'),
                            argument: 'APPROVED',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    group: 'billable-clients.filter.groups.invoice',
                    subgroup: 'invoice-status',
                    selector: 'invoices.yearlySequence',
                    comperator: '=noproforma=',
                    filters: [
                        {
                            label: t('billable-clients.filter.items.no-proforma-invoice'),
                            tooltip: 'billable-clients.filter.tool-tips.no-proforma-invoice',
                            argument: 'NO-PROFORMA-INVOICE',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    group: 'billable-clients.filter.groups.invoice',
                    label: t('billable-clients.filter.labels.fee-arrangement'),
                    selector: 'pricingLimitationTypeCode',
                    multiple: true,
                    filters: [
                        {
                            label: getClassifierText(classifiers['PRICING_LIMITATION_TYPE'], 'HOURLY_CAPPED'),
                            argument: 'HOURLY_CAPPED',
                        },
                        {
                            label: getClassifierText(classifiers['PRICING_LIMITATION_TYPE'], 'FIXED_ONETIME'),
                            argument: 'FIXED_ONETIME',
                        },
                        {
                            label: getClassifierText(classifiers['PRICING_LIMITATION_TYPE'], 'HOURLY_UNLIMITED'),
                            argument: 'HOURLY_UNLIMITED',
                        },
                        {
                            label: getClassifierText(classifiers['PRICING_LIMITATION_TYPE'], 'FIXED_RECURRING'),
                            argument: 'FIXED_RECURRING',
                        },
                    ],
                },
                //client section
                {
                    type: 'dynamicmultiselect',
                    subType: 'contact',
                    group: 'billable-clients.filter.groups.client',
                    label: t('billable-clients.filter.labels.client-name'),
                    selector: 'clientParty.id',
                    paginated: {
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'checkboxgroup',
                    group: 'billable-clients.filter.groups.client',
                    label: t('contacts.filter.internal-client'),
                    selector: 'clientParty.isInternalClient',
                    filters: [
                        {
                            label: t('yes'),
                            argument: 'true',
                        },
                    ],
                },
                //case section
                {
                    type: 'checkboxgroup',
                    selector: 'caseNumber',
                    comperator: '=ismycase=',
                    group: 'billable-clients.filter.groups.case',
                    filters: [
                        {
                            tooltip: 'matters.tooltips.my-cases',
                            label: t('matters.my-cases'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    selector: 'caseNumber',
                    comperator: '=ismyteamcase=',
                    group: 'billable-clients.filter.groups.case',
                    filters: [
                        {
                            tooltip: 'matters.tooltips.my-team-cases',
                            label: t('matters.my-team-cases'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'billable-clients.filter.groups.case',
                    label: t('billable-clients.filter.labels.case-number'),
                    selector: 'id',
                    getOptionStartAdornment: renderMatterIndicators,
                    paginated: {
                        fetch: useLazyGetMattersListQuery,
                        searchField: ['fullName', 'clientParty.name'],
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'fullName',
                        sort: ['clientParty.name,asc', 'name,asc'],
                        groupByField: 'clientParty.name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'billable-clients.filter.groups.case',
                    label: t('billable-clients.filter.labels.supervising-person'),
                    selector: 'supervisedByEmployee.id',
                    paginated: {
                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'billable-clients.filter.groups.case',
                    label: t('billable-clients.filter.labels.responsible-person'),
                    selector: 'responsibleEmployee.id',
                    paginated: {
                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'billable-clients.filter.groups.case',
                    label: t('billable-clients.filter.labels.handled-by'),
                    selector: 'handledByOfficeId',
                    paginated: {
                        fetch: useLazyGetMinOfficesWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                    getOptionEndAdornment: officeInactiveSuffix,
                },
                {
                    type: 'multiselect',
                    group: 'billable-clients.filter.groups.case',
                    label: t('billable-clients.filter.labels.invoice-template'),
                    selector: 'invoiceTemplateId',
                    options: getSortedFilterOptions('name', 'id', invoiceTemplateList ?? []),
                },
                {
                    type: 'daterange',
                    group: 'billable-clients.filter.groups.case',
                    selector: 'latestTimecardDate',
                    label: t('billable-clients.filter.labels.last-timecard-from'),
                    labelTo: t('billable-clients.filter.labels.last-timecard-until'),
                    shouldDisplayQuickPick: false,
                },
                {
                    type: 'daterange',
                    group: 'billable-clients.filter.groups.case',
                    selector: 'latestExpenseDate',
                    label: t('billable-clients.filter.labels.last-expense-from'),
                    labelTo: t('billable-clients.filter.labels.last-expense-until'),
                    shouldDisplayQuickPick: false,
                },
                {
                    type: 'daterange',
                    group: 'billable-clients.filter.groups.case',
                    selector: 'latestTimecardCreatedAt',
                    label: t('billable-clients.filter.labels.last-timecard-created-from'),
                    labelTo: t('billable-clients.filter.labels.last-timecard-created-until'),
                    tooltip: t('billable-clients.filter.tool-tips.last-timecard-created-from'),
                    tooltipTo: t('billable-clients.filter.tool-tips.last-timecard-created-until'),
                    shouldDisplayQuickPick: false,
                },
                {
                    type: 'daterange',
                    group: 'billable-clients.filter.groups.case',
                    selector: 'latestExpenseCreatedAt',
                    label: t('billable-clients.filter.labels.last-expense-created-from'),
                    labelTo: t('billable-clients.filter.labels.last-expense-created-until'),
                    tooltip: t('billable-clients.filter.tool-tips.last-expense-created-from'),
                    tooltipTo: t('billable-clients.filter.tool-tips.last-expense-created-until'),
                    shouldDisplayQuickPick: false,
                },
                {
                    type: 'checkboxgroup',
                    selector: 'id',
                    comperator: '=hasuninvoicedbillables=',
                    group: 'billable-clients.filter.groups.case',
                    multiple: true,
                    filters: [
                        {
                            label: t('billable-clients.filter.labels.has-uninvoiced-billable-timecards'),
                            argument: 'timecards',
                        },
                        {
                            label: t('billable-clients.filter.labels.has-uninvoiced-billable-expenses'),
                            argument: 'expenses',
                        },
                    ],
                },
                //other section
                {
                    type: 'checkboxgroup',
                    group: 'billable-clients.filter.groups.other',
                    selector: 'supervisedByEmployee.isActive',
                    filters: [
                        {
                            label: t('billable-clients.filter.items.inactive-supervising-partner'),
                            tooltip: 'billable-clients.filter.tool-tips.inactive-supervising-partner',
                            argument: 'false',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    group: 'billable-clients.filter.groups.other',
                    selector: 'responsibleEmployee.isActive',
                    filters: [
                        {
                            label: t('billable-clients.filter.items.inactive-responsible-lawyer'),
                            tooltip: 'billable-clients.filter.tool-tips.inactive-responsible-lawyer',
                            argument: 'false',
                        },
                    ],
                },
            ],
            searchBarSelectors: [
                { selector: 'name' },
                { selector: 'caseNumber' },
                { selector: 'responsibleEmployee.fullName' },
                { selector: 'supervisedByEmployee.fullName' },
                { selector: 'clientParty.name' },
            ],
        }),
        [classifiers, invoiceTemplateList]
    );

    const pluralTextId = new Map(
        Object.entries({
            default: 'billable-clients.cases-in-progress',
            filter: '=isbillable=',
            hasFilter: 'billable-clients.billable-cases',
        })
    );
    const singularTextId = new Map(
        Object.entries({
            default: 'billable-clients.case-in-progress',
            filter: '=isbillable=',
            hasFilter: 'billable-clients.billable-case',
        })
    );
    const noEntriesId = new Map(
        Object.entries({
            default: 'billable-clients.no-cases-in-progress-found',
            filter: '=isbillable=',
            hasFilter: 'billable-clients.no-billable-cases-found',
        })
    );

    if (isLoadingClassifiers || isLoadingInvoiceTemplates) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<BillableMatterListItem>
            data={data}
            isLoading={isFetching}
            pluralTextId={pluralTextId}
            singularTextId={singularTextId}
            noEntriesId={noEntriesId}
            fetchData={fetchData}
            fetchCount={useLazyGetBillableMattersCountQuery}
            filterConfig={filterConfig}
            rowsPerPage={50}
            renderCustomTable={renderMatterTable}
            renderCustomHeader={renderCustomHeader}
            disableUrlState={disableUrlState}
            initialSearchParams={initialSearchParams}
            selectedIds={selectedIds}
            onSelectionChange={onSelectionChange}
        />
    );
};

export default BillableMattersTable;
